/**
 * Framework-agnostic canvas template definitions.
 *
 * Shared between the web app (React + Lucide icons) and
 * the MCP App (vanilla TS + inline SVG). Icons are referenced
 * by name string; consumers map to their rendering approach.
 */

// Ontology namespaces (match the web app / Fuseki values)
const market = 'http://www.intellispot.com/ontology/market#'
const business = 'http://www.intellispot.com/ontology/business#'
const solution = 'http://www.intellispot.com/ontology/solution#'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CanvasSectionDef {
  id: string
  title: string
  /** Lucide icon name (lowercase, kebab-case) */
  iconName: string
  color: string
  bgColor: string
  classUris: string[]
  gridArea: string
  description: string
  sectionType?: 'default' | 'photo'
}

export interface GuidedQuestion {
  sectionId: string
  /** Question text — may contain {ref:sectionId} or {count:sectionId} placeholders */
  question: string
  hint: string
  fallbackSuggestions: string[]
  order: number
}

export interface ChallengeRule {
  id: string
  type: 'empty_section' | 'imbalance' | 'breadth_no_depth' | 'cross_section_coherence' | 'strategic_focus'
  severity: 'critical' | 'strategic' | 'opportunity'
  title: string
  /** Description template — may contain {ref:sectionId} or {count:sectionId} placeholders */
  descriptionTemplate: string
  affectedSection: string
}

export interface CanvasTemplateDef {
  id: string
  name: string
  description: string
  gridColumns: number
  gridRows: number
  /** CSS Grid template areas string */
  gridAreas: string
  sections: CanvasSectionDef[]
  /** Map of sectionId → array of related sections with descriptive labels for connections view */
  relatedSections?: Record<string, { target: string; label: string }[]>
  guidedQuestions?: GuidedQuestion[]
  challengeRules?: ChallengeRule[]
  storyPromptTemplate?: string
}

// ---------------------------------------------------------------------------
// Business Model Canvas
// ---------------------------------------------------------------------------

export const BUSINESS_MODEL_CANVAS: CanvasTemplateDef = {
  id: 'business-model-canvas',
  name: 'Business Model Canvas',
  description: 'Strategic management template for developing new or documenting existing business models',
  gridColumns: 10,
  gridRows: 6,
  gridAreas: `
    "partners partners activities activities value value relationships relationships segments segments"
    "partners partners activities activities value value relationships relationships segments segments"
    "partners partners resources resources value value channels channels segments segments"
    "partners partners resources resources value value channels channels segments segments"
    "costs costs costs costs costs revenue revenue revenue revenue revenue"
    "costs costs costs costs costs revenue revenue revenue revenue revenue"
  `,
  sections: [
    {
      id: 'key-partners',
      title: 'Key Partners',
      iconName: 'handshake',
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}KeyPartnership`],
      gridArea: 'partners',
      description: 'Network of suppliers and partners that make the business model work',
    },
    {
      id: 'key-activities',
      title: 'Key Activities',
      iconName: 'settings',
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}KeyActivity`],
      gridArea: 'activities',
      description: 'Most important things a company must do to make its business model work',
    },
    {
      id: 'key-resources',
      title: 'Key Resources',
      iconName: 'briefcase',
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}KeyResource`],
      gridArea: 'resources',
      description: 'Most important assets required to make a business model work',
    },
    {
      id: 'value-propositions',
      title: 'Value Propositions',
      iconName: 'heart',
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${business}ValueProposition`],
      gridArea: 'value',
      description: 'Bundle of products and services that create value for a specific Customer Segment',
    },
    {
      id: 'customer-relationships',
      title: 'Customer Relationships',
      iconName: 'message-square',
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}CustomerRelationship`],
      gridArea: 'relationships',
      description: 'Types of relationships a company establishes with specific Customer Segments',
    },
    {
      id: 'channels',
      title: 'Channels',
      iconName: 'truck',
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}Channel`],
      gridArea: 'channels',
      description: 'How a company communicates with and reaches its Customer Segments',
    },
    {
      id: 'customer-segments',
      title: 'Customer Segments',
      iconName: 'users',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}CustomerSegment`, `${market}Persona`],
      gridArea: 'segments',
      description: 'Different groups of people or organizations an enterprise aims to reach and serve',
    },
    {
      id: 'cost-structure',
      title: 'Cost Structure',
      iconName: 'wallet',
      color: '#847561',
      bgColor: '#f5f4f0',
      classUris: [`${business}CostStructure`],
      gridArea: 'costs',
      description: 'All costs incurred to operate a business model',
    },
    {
      id: 'revenue-streams',
      title: 'Revenue Streams',
      iconName: 'dollar-sign',
      color: '#847561',
      bgColor: '#f5f4f0',
      classUris: [`${business}RevenueStream`],
      gridArea: 'revenue',
      description: 'Cash a company generates from each Customer Segment',
    },
  ],
}

// ---------------------------------------------------------------------------
// Value Proposition Canvas
// ---------------------------------------------------------------------------

export const VALUE_PROPOSITION_CANVAS: CanvasTemplateDef = {
  id: 'value-proposition-canvas',
  name: 'Value Proposition Canvas',
  description: 'Tool to ensure product-market fit by mapping customer needs to value creation',
  gridColumns: 12,
  gridRows: 6,
  gridAreas: `
    "gains gains gains gains gains gains vp vp vp vp vp vp"
    "gains gains gains gains gains gains vp vp vp vp vp vp"
    "jobs jobs jobs jobs jobs jobs products products products products products products"
    "jobs jobs jobs jobs jobs jobs products products products products products products"
    "pains pains pains pains pains pains relievers relievers relievers relievers relievers relievers"
    "pains pains pains pains pains pains relievers relievers relievers relievers relievers relievers"
  `,
  sections: [
    {
      id: 'customer-jobs',
      title: 'Customer Jobs',
      iconName: 'target',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}CustomerJob`, `${market}Need`],
      gridArea: 'jobs',
      description: 'Tasks customers are trying to perform, problems they are trying to solve, or needs they want to satisfy',
    },
    {
      id: 'customer-gains',
      title: 'Gains',
      iconName: 'smile',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Gain`, `${market}Benefit`],
      gridArea: 'gains',
      description: 'Outcomes and benefits customers want - functional utility, social gains, positive emotions, cost savings',
    },
    {
      id: 'customer-pains',
      title: 'Pains',
      iconName: 'frown',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Pain`, `${market}Frustration`],
      gridArea: 'pains',
      description: 'Bad outcomes, risks, and obstacles related to customer jobs',
    },
    {
      id: 'value-propositions',
      title: 'Value Proposition',
      iconName: 'heart',
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${business}ValueProposition`],
      gridArea: 'vp',
      description: 'The bundle of products and services that create value for a specific customer segment',
    },
    {
      id: 'products-services',
      title: 'Products & Services',
      iconName: 'gift',
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${solution}Product`, `${solution}Service`, `${solution}Feature`],
      gridArea: 'products',
      description: 'List of all products and services a value proposition is built around',
    },
    {
      id: 'pain-relievers',
      title: 'Pain Relievers',
      iconName: 'zap',
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${solution}PainReliever`, `${solution}Solution`],
      gridArea: 'relievers',
      description: 'How products and services alleviate customer pains',
    },
  ],
}

// ---------------------------------------------------------------------------
// Positioning Canvas (April Dunford)
// ---------------------------------------------------------------------------

export const POSITIONING_CANVAS: CanvasTemplateDef = {
  id: 'positioning-canvas',
  name: 'Positioning Canvas',
  description: "April Dunford's positioning framework for defining how your product wins in the market",
  gridColumns: 4,
  gridRows: 6,
  gridAreas: `
    "product product product product"
    "product product product product"
    "category category category category"
    "alternatives attributes value whocares"
    "alternatives attributes value whocares"
    "alternatives attributes value whocares"
  `,
  sections: [
    {
      id: 'product',
      title: 'Product',
      iconName: 'package',
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${solution}Product`, `${solution}Service`],
      gridArea: 'product',
      description: 'Product name and one-line description - what you are positioning',
    },
    {
      id: 'market-category',
      title: 'Market Category',
      iconName: 'building-2',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Industry`, `${market}MarketCategory`],
      gridArea: 'category',
      description: 'The macro market and submarket that you compete in',
    },
    {
      id: 'competitive-alternatives',
      title: 'Competitive Alternatives',
      iconName: 'shuffle',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Competitor`, `${market}Alternative`],
      gridArea: 'alternatives',
      description: 'What would customers use if your product did not exist?',
    },
    {
      id: 'unique-attributes',
      title: 'Unique Attributes',
      iconName: 'sparkles',
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${solution}UniqueSellingPoint`, `${solution}Feature`, `${solution}Differentiator`],
      gridArea: 'attributes',
      description: 'What features/capabilities does your product have that the alternatives do not?',
    },
    {
      id: 'value',
      title: 'Value',
      iconName: 'heart',
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}ValueProposition`],
      gridArea: 'value',
      description: 'What value do those attributes enable for customers?',
    },
    {
      id: 'who-cares',
      title: 'Who Cares a Lot',
      iconName: 'users',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}CustomerSegment`, `${market}Persona`],
      gridArea: 'whocares',
      description: 'What are the characteristics of a customer that makes them care a lot about the value you deliver?',
    },
  ],
}

// ---------------------------------------------------------------------------
// Problem Statement Canvas
// ---------------------------------------------------------------------------

export const PROBLEM_STATEMENT_CANVAS: CanvasTemplateDef = {
  id: 'problem-statement-canvas',
  name: 'Problem Statement Canvas',
  description: 'Framework for discovering and refining problem statements before jumping to solutions',
  gridColumns: 6,
  gridRows: 4,
  gridAreas: `
    "context context problem problem alternatives alternatives"
    "context context problem problem alternatives alternatives"
    "customers customers emotional emotional shortcomings shortcomings"
    "customers customers quantifiable quantifiable shortcomings shortcomings"
  `,
  sections: [
    {
      id: 'context',
      title: 'Context',
      iconName: 'clock',
      color: '#6c757d',
      bgColor: '#f8f9fa',
      classUris: [`${market}Problem`],
      gridArea: 'context',
      description: 'When does the problem occur? What situation triggers it?',
    },
    {
      id: 'problem',
      title: 'Problem',
      iconName: 'alert-circle',
      color: '#dc3545',
      bgColor: '#ffeef0',
      classUris: [`${market}Problem`],
      gridArea: 'problem',
      description: 'What is the root cause of the problem?',
    },
    {
      id: 'alternatives',
      title: 'Alternatives',
      iconName: 'shuffle',
      color: '#6c757d',
      bgColor: '#f8f9fa',
      classUris: [`${market}Alternative`, `${market}Competitor`],
      gridArea: 'alternatives',
      description: 'What do customers do now to fix the problem?',
    },
    {
      id: 'customers',
      title: 'Customers',
      iconName: 'users',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}CustomerSegment`, `${market}Persona`],
      gridArea: 'customers',
      description: 'Who has the problem most often?',
    },
    {
      id: 'emotional-impact',
      title: 'Emotional Impact',
      iconName: 'heart',
      color: '#c9a227',
      bgColor: '#faf5e8',
      classUris: [`${market}CustomerPain`],
      gridArea: 'emotional',
      description: 'How does the customer feel?',
    },
    {
      id: 'quantifiable-impact',
      title: 'Quantifiable Impact',
      iconName: 'bar-chart-3',
      color: '#4a90a4',
      bgColor: '#e6f3f7',
      classUris: [`${market}CustomerPain`],
      gridArea: 'quantifiable',
      description: 'What is the measurable impact (include units)?',
    },
    {
      id: 'alternative-shortcomings',
      title: 'Alternative Shortcomings',
      iconName: 'alert-triangle',
      color: '#dc3545',
      bgColor: '#ffeef0',
      classUris: [`${market}AlternativeDisadvantage`],
      gridArea: 'shortcomings',
      description: 'What are the disadvantages of the alternatives?',
    },
  ],
}

// ---------------------------------------------------------------------------
// Persona Canvas
// ---------------------------------------------------------------------------

export const PERSONA_CANVAS: CanvasTemplateDef = {
  id: 'persona-canvas',
  name: 'Persona Canvas',
  description: 'Understand your customer deeply - their environment, needs, fears and hopes',
  gridColumns: 6,
  gridRows: 6,
  gridAreas: `
    "negative negative photo photo positive positive"
    "negative negative photo photo positive positive"
    "headaches headaches name name opportunities opportunities"
    "headaches headaches name name opportunities opportunities"
    "fears fears need need hopes hopes"
    "fears fears need need hopes hopes"
  `,
  sections: [
    {
      id: 'negative-trends',
      title: 'Negative Trends',
      iconName: 'cloud',
      color: '#847561',
      bgColor: '#f5f4f0',
      classUris: [`${market}MarketTrend`, `${market}Threat`],
      gridArea: 'negative',
      description: 'Negative trends from the environment affecting this persona',
    },
    {
      id: 'positive-trends',
      title: 'Positive Trends',
      iconName: 'sun',
      color: '#847561',
      bgColor: '#f5f4f0',
      classUris: [`${market}MarketTrend`, `${market}Opportunity`],
      gridArea: 'positive',
      description: 'Positive trends from the environment benefiting this persona',
    },
    {
      id: 'photo',
      title: 'Photo',
      iconName: 'user',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Persona`],
      gridArea: 'photo',
      description: 'Visual representation of the persona',
      sectionType: 'photo',
    },
    {
      id: 'name-role',
      title: 'Name & Role',
      iconName: 'user',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Persona`],
      gridArea: 'name',
      description: 'The persona name and their role/job title',
    },
    {
      id: 'headaches',
      title: 'Headaches',
      iconName: 'frown',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Pain`, `${market}Frustration`],
      gridArea: 'headaches',
      description: 'Professional and work related issues they face',
    },
    {
      id: 'opportunities',
      title: 'Opportunities',
      iconName: 'smile',
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Opportunity`, `${market}Gain`],
      gridArea: 'opportunities',
      description: 'Professional and work related positive outcomes they seek',
    },
    {
      id: 'fears',
      title: 'Fears',
      iconName: 'alert-triangle',
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${market}Fear`, `${market}Risk`],
      gridArea: 'fears',
      description: 'Personal fears and anxieties',
    },
    {
      id: 'need',
      title: 'Need',
      iconName: 'heart',
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${market}Need`, `${market}CustomerJob`],
      gridArea: 'need',
      description: 'What does this person really want? Their core need.',
    },
    {
      id: 'hopes',
      title: 'Hopes',
      iconName: 'star',
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${market}Hope`, `${market}Aspiration`],
      gridArea: 'hopes',
      description: 'Personal goals and hopes for the future',
    },
  ],
}

// ---------------------------------------------------------------------------
// All templates + lookup
// ---------------------------------------------------------------------------

export const CANVAS_TEMPLATES: CanvasTemplateDef[] = [
  BUSINESS_MODEL_CANVAS,
  VALUE_PROPOSITION_CANVAS,
  POSITIONING_CANVAS,
  PROBLEM_STATEMENT_CANVAS,
  PERSONA_CANVAS,
]

export function getCanvasTemplateDef(id: string): CanvasTemplateDef | undefined {
  return CANVAS_TEMPLATES.find((t) => t.id === id)
}
